import { 
  Button, 
  Card, 
  CardHeader, 
  Drawer, 
  DrawerBody, 
  DrawerHeader, 
  DrawerHeaderTitle, 
  Title3,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Text,
  Subtitle2,
  Spinner,
  Dropdown,
  Option,
  Combobox,
  Tag,
  TagGroup,
  Switch,
  Badge,
  Tooltip
} from "@fluentui/react-components";
import { ArrowLeftRegular, ArrowRightRegular, DismissRegular, Edit12Regular, Edit16Regular, EditRegular, PeopleTeamRegular, PersonAddRegular, SearchRegular } from "@fluentui/react-icons";
import { User } from "../../models/users";
import { useEffect, useState } from "react";
import { getUsers } from "../../services/usersService";
import UserDrawer from "./UserDrawer";
import { set } from "react-hook-form";
import SearchUserDialog from "../dialogs/SearchUserDialog";
import Paginator from "../../utils/Paginator";
import { features } from "process";
import { useAppContext } from "../../AppContext";
import CreateUserDialog from "../dialogs/CreateUserDialog";
import UsersByRoleDialog from "../dialogs/UsersByRoleDialog";
import { getOrganizations } from "../../services/organizationService";
import { Organization } from "../../models/organizations";
import { RoleDTO } from "../../models/role";
import { Module } from "../../models/module";

type Props = {
  onClose: () => void;
  open: boolean;
};

const UsersDrawer = (props: Props) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [userCount, setUserCount] = useState<number>(0);
  const [searchString, setSearchString] = useState<string>("");
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState<boolean>(false);
  const [isCreateUserDialogOpen, setIsCreateUserDialogOpen] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [orgSearch, setOrgSearch] = useState<string>(""); // No longer needed with Combobox, but keep for value
  const [includeDisabled, setIncludeDisabled] = useState<boolean>(false);
  const [isUsersByRoleOpen, setIsUsersByRoleOpen] = useState<boolean>(false);

  const { user, featurePermissions } = useAppContext();
  // The role overview is for those who manage a ramavtal (General, Ramavtalsansvarig, Kontoadmin)
  const canSeeUsersByRole = !!user && (
    user.roles.some((r: RoleDTO) => r.roleType === 'General')
    || user.assignments?.some((a: any) => a.role.roleName === 'Ramavtalsansvarig' || a.role.roleName === 'Kontoadmin'));
  const canReactivate = featurePermissions?.some(fp => fp.action == 'Reactivate user') ?? false;

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const isGeneral = user.roles.some((r: RoleDTO) => r.roleType == 'General');
      let userOrganizations: number[] = [];
      let userModules: number[] = [];
      // Kommun (Procurer) users only see users in their own organization
      if(!isGeneral && user.roles.some((r: RoleDTO) => r.roleType == 'Procurer') && user.organization){
        userOrganizations = [user.organization.organizationID];
      }
      if(user.roles.some((r: RoleDTO) => r.roleType == 'Supplier' && !user.organization)){
        userModules = featurePermissions?.filter(fp => fp.action == 'See user menu').flatMap(fp => fp.moduleID) || [];
      }
      const usersData = await getUsers(isGeneral && selectedOrganization ? +selectedOrganization : userOrganizations, userModules, page, searchString, canReactivate && includeDisabled);
      setUsers(usersData.users);
      setUserCount(usersData.count);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch users when the drawer is opened
    if (props.open) {
      fetchUsers();
    }
  }, [props.open, page, searchString, selectedOrganization, includeDisabled]);

  useEffect(() => {
    setSelectedOrganization("");
    setOrgSearch("");

    const fetchOrganizations = async () => {
      if(user.roles.some((r: RoleDTO) => r.roleType == 'General')){
        try{
          const orgs = await getOrganizations();
          setOrganizations(orgs.sort((a:Organization, b:Organization) => a.organizationName.localeCompare(b.organizationName)));
        } catch (error) {
          console.error('Error fetching organizations:', error);
        }
      }
      else if(user.organization){
        setOrganizations([user.organization]);
      }
    }
    if(props.open){
      fetchOrganizations();
    }
  }, [props.open]);

  const formatLastActivity = (lastActivity: string | Date | undefined) => {
    if (!lastActivity) return 'Aldrig';
    
    const date = typeof lastActivity === 'string' ? new Date(lastActivity) : lastActivity;
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleUserUpdated = async (updatedUser: User) => {
    // Refetch users to get the updated paginated list
    await fetchUsers();
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <>
    <Drawer
      size="large"
      position="end"
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
      modalType="non-modal"
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          <div className="flex items-start justify-between w-full">
            <h2>
              Användare
            </h2>
            <Button size="small" icon={<DismissRegular />} shape="circular" onClick={props.onClose} />
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className="flex flex-col gap-4">
          {/* Row 1: actions, one line each */}
          <div className="flex gap-2 mt-5 items-center flex-wrap">
            <Button style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setIsSearchDialogOpen(true)} icon={<SearchRegular />} appearance="primary">
              Sök användare
            </Button>
            {user && (user.roles.some((role: RoleDTO) => (role.roleType === "General") || (role.roleName === "Föreståndare")) || featurePermissions?.some(p => p.action == 'Create user')) && <Button style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setIsCreateUserDialogOpen(true)} icon={<PersonAddRegular />}>
              Skapa användare
            </Button>}
            {canSeeUsersByRole && (
              <Tooltip
                relationship="description"
                content="Se vilka användare som har vilken roll i dina ramavtal, och kopiera eller ladda ner e-postlistor."
              >
                <Button style={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={() => setIsUsersByRoleOpen(true)} icon={<PeopleTeamRegular />}>
                  Användare per roll
                </Button>
              </Tooltip>
            )}
            {canReactivate && (
              <Switch
                className="ml-auto"
                label="Visa inaktiva användare"
                checked={includeDisabled}
                onChange={(_e, data) => { setIncludeDisabled(data.checked); setPage(0); }}
              />
            )}
          </div>
          {/* Row 2: filters (organisation picker is for sysadmins only) */}
          {user && organizations && user.roles.some((r: RoleDTO) => r.roleType === 'General') && (
          <div className="flex gap-2">
            {(
              <div style={{ flex: 1, minWidth: 0 }}>
                <Combobox
                  style={{ width: '100%' }}
                  placeholder="Välj organisation"
                  value={organizations.find(o => o.organizationID.toString() === selectedOrganization)?.organizationName || orgSearch}
                  onOptionSelect={(_event, data) => {
                    setSelectedOrganization(data.optionValue || "");
                    if (!data.optionValue) setOrgSearch("");
                  }}
                  positioning={{autoSize: false}}
                  onInput={e => {
                    const value = e.currentTarget.value;
                    setOrgSearch(value);
                    if (value === "") {
                      setSelectedOrganization("");
                      setOrgSearch("");
                    }
                  }}
                  listbox={{
                    style: {
                      maxHeight: '300px',
                      overflowY: 'auto'
                    },
                  }}
                  clearable
                >
                  {organizations
                    .filter(org => org.organizationName.toLowerCase().includes(orgSearch.toLowerCase()))
                    .map((org) => (
                      <Option key={org.organizationID} value={org.organizationID.toString()}>
                        {org.organizationName}
                      </Option>
                    ))}
                </Combobox>
              </div>
            )}
          </div>
          )}
          {searchString && (
            <div className="flex items-center gap-2">
              <Text size={200} weight="semibold">Aktiv sökfilter:</Text>
              <TagGroup
                onDismiss={() => { setSearchString(""); setPage(0); }}
              >
                <Tag
                  dismissible
                  dismissIcon={{ "aria-label": "Ta bort filter" }}
                  value="search"
                  size="small"
                  appearance="brand"
                >
                  {searchString}
                </Tag>
              </TagGroup>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner size="large" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className="w-2/5">
                      <Subtitle2>E-post</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/5">
                      <Subtitle2>Förnamn</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-1/5">
                      <Subtitle2>Efternamn</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-[20%]">
                      <Subtitle2>Senaste inloggning</Subtitle2>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-12">
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length > 0 ? (
                    users.map((user, index) => (
                      <TableRow key={user.userID || index} className="group">
                        <TableCell className="w-2/5">
                          <Text>{user.email.length > 40 ? user.email.slice(0, 40) + '...' : user.email}</Text>
                          {user.isDisabled && <Badge className="ml-2" color="danger" appearance="tint" size="small">Inaktiv</Badge>}
                        </TableCell>
                        <TableCell className="w-1/5">
                          <Text>{user.firstName}</Text>
                        </TableCell>
                        <TableCell className="w-1/5">
                          <Text>{user.lastName}</Text>
                        </TableCell>
                        <TableCell className="w-[15%]">
                          <Text>{formatLastActivity(user.lastActivity)}</Text>
                        </TableCell>
                        <TableCell className="w-12">
                          <Button
                            icon={<Edit16Regular />}
                            shape="circular"
                            appearance="primary"
                            onClick={() => {if(featurePermissions?.some(p => p.action == 'Open user')){setSelectedUser(user); setIsUserDrawerOpen(true);}}}
                            title="Redigera användare"
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className="flex justify-center py-8">
                          <Text>Inga användare hittades</Text>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {/* <div className="flex justify-between mt-4">
                <div>Antal objekt per sida: 50</div>
                <div className='flex gap-2 items-center'>
                  <Button disabled={page == 0} onClick={() => setPage(page - 1)} icon={<ArrowLeftRegular />}></Button>
                  <Button disabled={userCount < 50 + 50*page} onClick={() => setPage(page + 1)} icon={<ArrowRightRegular />}></Button>
                  {1+(50*page)} - {(userCount > (50+(50*page))) ? 50+(50*page) : userCount} av {userCount}
                </div>
              </div> */}
              <Paginator currentPage={page} totalCount={userCount} onPageChange={handlePageChange} />
            </div>
          )}
        </div>
      </DrawerBody>
    </Drawer>
    {selectedUser && <UserDrawer 
      open={isUserDrawerOpen} 
      onClose={() => {setIsUserDrawerOpen(false); setSelectedUser(null);}}
      organizations={organizations}
      user={selectedUser} 
      onUserUpdated={handleUserUpdated} 
    />}
    <SearchUserDialog 
      open={isSearchDialogOpen}
      onClose={() => setIsSearchDialogOpen(false)}
      onSearch={(searchString: string) => {setSearchString(searchString); setPage(0);}}
    />
    <UsersByRoleDialog
      open={isUsersByRoleOpen}
      onClose={() => setIsUsersByRoleOpen(false)}
    />
    <CreateUserDialog
      open={isCreateUserDialogOpen}
      onClose={() => setIsCreateUserDialogOpen(false)}
      onUserUpdated={handleUserUpdated}
    />
  </>
  );
};

export default UsersDrawer;